import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import axios from 'axios';

const DatabaseSetup = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupType, setSetupType] = useState(null);
  const [setupMessage, setSetupMessage] = useState(null);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/setup/status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setStatus(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSetup = async (type) => {
    setSetupLoading(true);
    setSetupType(type);
    setSetupMessage(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/setup/${type}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setSetupMessage({ type: 'success', text: response.data.message });
      fetchStatus(); // Refresh status
    } catch (err) {
      setSetupMessage({ 
        type: 'error', 
        text: err.response?.data?.message || `Error setting up ${type}`
      });
    } finally {
      setSetupLoading(false);
      setSetupType(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Database Setup
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {setupMessage && (
        <Alert 
          severity={setupMessage.type} 
          sx={{ mb: 3 }}
          onClose={() => setSetupMessage(null)}
        >
          {setupMessage.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Table Status
        </Typography>
        <List>
          {Object.entries(status?.tables || {}).map(([table, exists]) => (
            <ListItem key={table}>
              <ListItemText 
                primary={table} 
                secondary={`Records: ${status?.counts?.[table] || 0}`}
              />
              <Chip 
                label={exists ? 'Exists' : 'Missing'} 
                color={exists ? 'success' : 'error'}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={() => handleSetup('payments')}
          disabled={setupLoading}
        >
          {setupLoading && setupType === 'payments' ? 
            <CircularProgress size={24} /> : 
            'Setup Payments Table'
          }
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSetup('subscriptions')}
          disabled={setupLoading}
        >
          {setupLoading && setupType === 'subscriptions' ? 
            <CircularProgress size={24} /> : 
            'Setup Subscriptions Table'
          }
        </Button>
      </Box>
    </Box>
  );
};

export default DatabaseSetup; 